import cloneDeep from 'lodash/cloneDeep';

import setDesignDataOperation from 'editor/src/store/design/operation/setDesignDataOperation';
import getDesignData from 'editor/src/store/design/selector/getDesignData';
import { DesignData } from 'editor/src/store/design/types';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getTemplates from 'editor/src/store/editorModules/templates/selector/getTemplates';
import { Thunk } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { captureException } from 'editor/src/logger';
import getReflectContext from 'editor/src/util/reflectDesignData/getReflectContext';
import reflectMediaElements from 'editor/src/util/reflectDesignData/reflectMediaElements';
import reflectSpreadAndApply from 'editor/src/util/reflectDesignData/reflectSpreadAndApply';

const applyDesignTemplateByIdOperation =
  (templateId: string, onApplyCallback?: (design: DesignData) => void): Thunk =>
  (dispatch, getState: () => RootState) => {
    const state = getState();
    const template = getTemplates(state).find((template) => template.id === templateId);
    if (!template) {
      const error = new Error(`Can not find template by id ${templateId}`);
      captureException(error);
      throw error;
    }

    const destDesign = getDesignData(state);

    if (!destDesign) {
      return;
    }

    const newDesignData: DesignData = cloneDeep(destDesign);

    const currentSpreadIndex = getCurrentSpreadIndex(state);
    const destSpread = newDesignData?.spreads[currentSpreadIndex];
    const sourceDesign = template.structure;
    const sourceSpread = sourceDesign.spreads[0];

    if (!sourceSpread || !destDesign || !destSpread) {
      return;
    }

    const context = getReflectContext(state);
    reflectSpreadAndApply({
      sourceSpread,
      destSpread,
      sourceDesign,
      newDesign: newDesignData,
      sourceSpreadIndex: 0,
      context,
      mode: 'adapt',
    });

    // The shared reflection takes media from the first page only. A layout that also carries media on a
    // later page of its spread puts that media back on the matching page.
    const mediaContext = (context.fonts || context.gridDesigns) && {
      fonts: context.fonts || [],
      gridDesigns: context.gridDesigns || [],
    };
    sourceSpread.pages.forEach((sourcePage, pageIndex) => {
      const destPage = destSpread.pages[pageIndex];
      if (pageIndex === 0 || !destPage || !sourcePage.groups.media?.length) {
        return;
      }
      destPage.groups.media = reflectMediaElements(
        sourceSpread,
        sourcePage,
        destSpread,
        context.layouts,
        'adapt',
        0,
        sourceDesign,
        newDesignData,
        mediaContext,
      );
    });

    void dispatch(setDesignDataOperation(newDesignData, true, onApplyCallback));
  };

export default applyDesignTemplateByIdOperation;
